/**
 * A reference points to a definition in a different location of a template using an anchor. A reference can further use
 * paths to traverse the template relative to the anchor. References are extracted from value strings present in the template.
 *
 * @example
 * ```ts
 * // Source string:
 * `$data.myPath.myValue`
 *
 * // Reference:
 * {
 *  anchor: '$data',
 *  path: ['myPath', 'myValue'],
 * }
 * ```
 */
export type Reference = {
  anchor: string;
  path: string[];
};

/**
 * Error types that can occur when parsing a reference.
 */
export enum ReferenceParseError {
  InvalidPath = 'The path associated with the reference is not parseable.',
  IsMapType = 'Entry is of type map.',
  DeclarationNotFound = 'Could not find declaration.',
}

/**
 * Search result types that can occur when searching for a reference.
 * - `skipped`: The reference was skipped, this can occur when a reference is defined by the templating language itself for example.
 * - `found`: The reference was found.
 */
export enum SearchResultType {
  Skipped = 'skipped',
  Found = 'found',
}

/**
 * Wrapper for the search result of a reference.
 */
export type SearchResult =
  | {
      type: SearchResultType.Found;
      id: string;
    }
  | {
      type: SearchResultType.Skipped;
    };

/**
 * Resolve result types that can occur when resolving a reference.
 */
export enum ReferenceResolveType {
  Skipped = 'skipped',
  Found = 'found',
  Errored = 'errored',
}

/**
 * Wrapper for the resolve result of a reference.
 */
export type ReferenceResolveResult =
  | {
      type: ReferenceResolveType.Found;
      id: string;
    }
  | {
      type: ReferenceResolveType.Skipped;
    }
  | {
      type: ReferenceResolveType.Errored;
      error: ReferenceParseError;
    };
